/** @module csi/api */
define([
	"dojo/_base/lang",
	"dojo/sniff",
	"csi",
	"csi/_base/underscore",
	"csi/access/_base",
	"csi/canvas/storeFactory",
	"csi/config/preferences",
	"csi/core/ExtendedPath",
	"csi/core/extendedPathService",
	"csi/image/paths",
	"csi/locale",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/types",
	"csi/utility/apiUtility",
	"csi/xml",
	"csi/view/_query/Result",
	"csi/view/Toolbar",
	"designSystem/settings",
], function(lang, sniff, csi, _, access, storeFactory, configPreferences, ExtendedPath, {parseExtendedPath}, imagePaths, locale, reflection, reflectionHints, 
	sessionConstants, {sessionStore}, types, apiUtility, xml, QueryResult, Toolbar, designSystemSettings) {

	// reference: https://centric8.atlassian.net/wiki/display/AAT/Site.js

	var DEFAULT_BROWSER = "Browser";
	var DEFAULT_PAPER = "Paper";
	var CHROME = "chrome";
	var FF = "ff";
	var PDF = "pdf";
	var SAFARI = "safari";
	var MAX_CANVAS_LIMIT = 200;

	var _addedNumberFormats = [];

	// String: the current browser being used
	var _browser;

	// Number: limit to maximum number of canvas pages
	var _canvasPageLimit = 30;

	/** @property {number} _canvasQualityFactor - canvas snapshot quality factor */
	let _canvasQualityFactor = 1;

	// String function(/*String*/): function that takes a string of HTML content and returns the new HTML content -
	// necessary to convert external URLs (outside of customer network) to internal URLs (inside network) in HTML content
	// passed for ad-hoc PDFs
	var _externalToInternalUrlFunction;

	// String[]: list of types shown in image browser Types pane
	var _imageTypes = [
		"ColorMaterial",
		"ColorSpecification",
		"Colorway",
		"Material",
		"Style",
		"Theme"
	];

	// String[]: list of remove panes in image browser - valid values (for now just one): "Site"
	var _removedImagePanes = [];

	// Object: print margins
	// 		Browsers: pdf, ff, chrome, safari
	// 		Paper: A3, A4, A5, Letter, Legal, Tabloid
	//		Orientation: Landscape, Portrait
	var _margins = {
		Browser: {	// chrome, ff
			Paper: {
				Landscape: {
					h: 0.32,
					w: 0.32
				},
				Portrait: {
					h: 0.32,
					w: 0.32
				}
			}
		},
		pdf: {
			Paper: {
				Landscape: {
					h: 0.24,
					w: 0.32
				},
				Portrait: {
					h: 0.24,
					w: 0.32
				}
			}
		},
		safari: {
			Paper: {
				Landscape: {
					h: 0.52,
					w: 0.5
				}
			}
		}
	};

	// Object: similar to margins above, this stores scale factors to use for portrait printing;
	// ff, and safari all seem to work as expected and require no extra scaling
	var _portraitScaling = {
		chrome: {
			h: 1.13,
			w: 1.15
		},
		pdf: {
			h: 1.13,
			w: 1.15
		}
	};

	// Object: similar to scaling above, but applied to data sheets as zoom factor for portrait printing;
	// data sheets must be authored in consistent size, so cannot scale as in case with standard (non-print) views
	var _portraitZoom = {
		chrome: 1.1,
		ff: 1,
		pdf: 1,
		safari: 1.05
	};

	var _nodeTypesToLog = []; // node types to log

	var _persistFieldsForSaveAndNew = {};

	/** setting to allow natural horizontal scrolling in tables */
	let _allowNonFrozenTables = false;

	/**
	 * Override of 6.5 Comment Delete behavior
	 *
	 * In 6.5, Gallery comment delete access is only allowed to the author of the comment
	 * Pre 6.5, Gallery comment delete access is allowed if you are an Admin or are the author and this is the last post
	 *
	 * By default, use the new 6.5 behavior - plan is to allow override in Site.js so customers can customize handling
	 * without adding UI requirements for reverting behavior
	 *
	 * false to use 6.5+ behavior, true to use pre 6.5 behavior
	 */
	var _useLegacyCommentDeleteAccessBehavior = false;

	/**
	 * Override of 6.6 Samples Quantity Max Limit
	 *
	 * 6.6 and previously had a hard coded maximum of 10 (Apparel/SamplesForm.js) for the number of Samples to create
	 * allow api change to other amount (e.g. 100)
	 */
	var _maxQuantitySamples = 10;

	/**
	 * Override of 6.3+ "Select All"/"None" option for SKU creation to allow for creating
	 * SKUs from just color or size without being required to choose the other
	 */
	var _allowNoneOptionForSKUCreation = false;

	var _filterRemember = true;

	/**
	 * Set width for "Sizes" popup dialogs. Dialog open from Size Charts table, column "Sizes" - little action icon inside the column.
	 */
	var _sizePopupColumnWidth = 0

	/**
	 * Add contextual path hints to the system
	 * @param {string} pathHintsId - id for contextual path hints
	 * @param {string[]} hintPaths - path must include class names (typed path syntax)
	 * @param {string[]} [omitPaths] - path must include class names (typed path syntax)
	 */
	function addContextualPathHints(pathHintsId, hintPaths, omitPaths) {
		try {
			configPreferences.addContextualPathHints(pathHintsId, hintPaths, omitPaths);
		} catch (ex) {
			console.error(`addContextualPathHints with id "${pathHintsId}" encountered error: ${ex.message}`);
		}
	}

	/**
	 * Add omit path to the system
	 * @param {string} typedPath - path must include class names (typed path syntax)
	 */
	function addOmitPath(typedPath) {
		try {
			configPreferences.addHiddenPath(parseExtendedPath(typedPath));
		} catch (ex) {
			console.error("addOmitPath(" + typedPath + "): " + ex.message);
		}
	}

	/**
	 * Add global path hint to the system
	 * @param {string} typedPath - path must include class names (typed path syntax)
	 */
	function addPathHint(typedPath) {
		try {
			configPreferences.addPathHints([typedPath]);
		} catch (ex) {
			console.error("addPathHint(" + typedPath + "): " + ex.message);
		}
	}

	/**
	 * Configure availability of reference documents on given type
	 * @param {string} type - domain object type
	 * @param {boolean} allow
	 */
	function allowReferenceDocumentsOnType(type, allow) {
		var rc = reflection.getClass(type);
		if (!rc) {
			console.error("allowReferenceDocumentsOnType(" + type + "): type not found");
			return;
		}

		if (!rc.isDerivedFrom("DocAndCommentOwner")) {
			console.error("allowReferenceDocumentsOnType(" + type + "): type does not contain documents");
			return;
		}

		reflectionHints.allowReferenceDocumentsOnType(type, allow);
	}

	/**
	 * Configure availablity of 3D documents on given type
	 * @param {string} type - domain object type
	 * @param {boolean} allow
	 */
	function allow3DDocumentOnType(type, allow) {
		apiUtility.deprecated("api.allow3DDocumentOnType is deprecated; 3D Document upload is now done through the default Document upload action and is allowed on any type that allows Documents.");
	}

	/**
	 * Configure availablity of 3D material on given type
	 * @param {string} type - domain object type
	 * @param {boolean} allow
	 */
	function allow3DMaterialOnType(type, allow) {
		var rc = reflection.getClass(type);

		if (!sessionConstants.allow3d) {
			console.error("3D Functions are not enabled. Please contact your Administrator.");
			return;
		}

		if (!rc) {
			console.error("allow3DMaterialOnType(" + type + "): type not found");
			return;
		}

		if (!rc.isDerivedFrom("DocAndCommentOwner")) {
			console.error("allow3DMaterialOnType(" + type + "): type does not contain material documents");
			return;
		}

		reflectionHints.allow3DMaterialOnType(type, allow);
	}

	/**
	 * Register new module with system
	 * @param {string} moduleId - example is Site/MyModule
	 * @param {string[]} moduleArray - array of module dependencies
	 * @param {Function} moduleFunction - function to create new module, taking all resolved module dependencies as paramters
	 */
	function defineModule(moduleId, moduleArray, moduleFunction) {
		var moduleResult = csi.contextRequire(moduleArray, moduleFunction);
		csi.registerWebpackModule(moduleId, null, moduleResult);
	}

	/**
   * Return true if non-frozen tables are allowed
   * @return {boolean}
   */
	function allowNonFrozenTables() {
		return _allowNonFrozenTables;
	}

	/**
	 * When using relative filters while using Compare feature, compare independently, not solely relative to the origin (current)
	 */
	function disableCompareRelativeToOrigin() {
		sessionStore.disableCompareRelativeToOrigin = true;
	}

	/**
	 * Enable new delayed search experience for attribute on given node type
	 * @param {string} type
	 * @param {string} attrId
	 */
	function enableDelayedSearchForAttribute(type, attrId) {
		apiUtility.deprecated(`api.enableDelayedSearchForAttribute is deprecated; set Delayed Search to true for attribute "${attrId}" on Business Object "${reflection.getClass(type).alias}"`);
	}

	/**
	 * Enable non-frozen tables that will scroll horizontally naturally (real scrollbar)
	 */
	function enableNonFrozenTables() {
		_allowNonFrozenTables = true;
	}


	/**
	 * Enable actions to create nodes per group when BOM is grouped by type
	 */
	function enableBomGroupByTypeNewActions() {
		sessionStore.allowBomGroupByTypeNewActions = true;
	}

	/**
	 * Return browser currently in use
	 */
	function _getBrowser(forPdf) {
		if (forPdf) {
			return PDF;
		}

		if (!_browser) {
			if (sniff(CHROME)) {
				// this is true for Edge too
				_browser = CHROME;
			} else if (sniff(FF)) {
				_browser = FF;
			} else if (sniff(SAFARI)) {
				_browser = SAFARI;
			}
		}

		return _browser;
	}

	return {
		CHROME,
		FF,
		PDF,
		SAFARI,

		LANDSCAPE: "Landscape",
		PORTRAIT: "Portrait",

		addContextualPathHints,
		addOmitPath,
		addPathHint,
		addLinkValidation: reflectionHints.addLinkValidation,
		allow3DDocumentOnType,
		allow3DMaterialOnType,
		allowNonFrozenTables,
		allowReferenceDocumentsOnType,
		defineModule,
		disableCompareRelativeToOrigin,
		enableBomGroupByTypeNewActions,
		enableDelayedSearchForAttribute,
		enableNonFrozenTables,
		addCommentPath: reflectionHints.addCommentPath,
		removeCommentPath: reflectionHints.removeCommentPath,

		/**
		 * Add path used to populate canvas placeholders.
		 * @property {string} sourceType - type used to begin the search for paths
		 * @property {string} targetType - type to refine the search for paths
		 * @property {string} path - a single path used to perform the query
		 * @property {string} pathFunction - function receiving the node, to determine path returned
		 * @property {string} filterQuery - optional search query
		 * To add multiple paths, call once per path.
		 */
		addCanvasPlaceholderPath: function(sourceType, targetType, path, pathFunction, filterQuery) {
			if (!sourceType || !targetType) {
				console.warn("addCanvasPlaceholderPath - sourceType and targetType are required");
				return;
			}

			if (!pathFunction && !filterQuery && !path) {
				console.warn("addCanvasPlaceholderPath - one of pathFunction, filterQuery or path must be provided");
				return;
			}

			if (path && pathFunction) {
				console.warn("addCanvasPlaceholderPath - if path is provided, pathFunction should not be provided");
				return;
			}

			if (path) {
				// Validate path
				if (!ExtendedPath.create(sourceType, path)) {
					console.warn("addCanvasPlaceholderPath - invalid path: ", path);
					return;
				}
			}
			storeFactory.add(sourceType, targetType, path, pathFunction, filterQuery);
		},

		/**
		 * Remove path used to populate canvas placeholders.
		 * @property {string} sourceType - type used to begin the search for paths
		 * @property {string} targetType - type to refine the search for paths
		 */
		removeCanvasPlaceholderPath: function(sourceType, targetType) {
			if (!sourceType) {
				console.warn("removeCanvasPlaceholderPath - sourceType is not defined, and is required");
				return;
			}
			storeFactory.remove(sourceType, targetType);
		},

		/**
		 * Add a type used to populate canvas right menu reference data accordions.
		 * @param {string} sourceType - canvas type where this target will show up
		 * @param {string} targetType - target type to show in the right side menu of canvas reference data
		 */
		addReferenceDataType: function(sourceType, targetType) {
			if (!sourceType || !targetType) {
				console.warn("addReferenceDataType - sourceType and targetType are required");
				return;
			}

			storeFactory.addReferenceDataType(sourceType, targetType);
		},

		/**
		 * Remove reference data type.
		 * If no targetType is specified, all targets on source will be removed.
		 * @param {string} sourceType -  canvas type where this target shows up
		 * @param {string} [targetType] - type that is showing up in the accordion
		 */
		removeReferenceDataType: function(sourceType, targetType) {
			if (!sourceType) {
				console.warn("removeReferenceDataType - sourceType is not defined, and is required");
				return;
			}
			storeFactory.removeReferenceDataType(sourceType, targetType);
		},

		addImagePath: function(/*String*/type, /*String*/path) {
			imagePaths.add(type, path);
		},

		removeImagePath: function(/*String*/type, /*String*/path) {
			imagePaths.remove(type, path);
		},

		addImageType: function(/*String*/type) {
			_imageTypes = _.union(_imageTypes, [type]);
		},

		removeImageType: function(/*String*/type) {
			_imageTypes = _.without(_imageTypes, type);
		},

		removeImagePane: function(/*String*/name) {
			_removedImagePanes.push(name);
		},

		addNumberFormat: function(/*String*/fmt, /*Boolean*/bInteger) {
			types.addFormat(bInteger ? xml.ATTR_INTEGER : xml.ATTR_DOUBLE, fmt);
			_addedNumberFormats.push(fmt);
		},

		getAddedNumberFormats: function() {
			return _addedNumberFormats;
		},

		addTimeFormatOptions() {
			apiUtility.deprecated("api.addTimeFormatOptions is deprecated; please use the locale settings UI instead.");
		},

		addUserDefault() {
			apiUtility.deprecated("api.addUserDefault is deprecated; please use user default UI instead.")
		},

		excludeActionFromEllipses: function(/*String*/actionName) {
			// summary: add action to "Exclude from ellipses" list
			QueryResult.excludeActionFromEllipses(actionName);
		},

		/**
		 * Give the canvas page limit number
		 * @return {number} - canvas page limit
		 */
		getCanvasPageLimit: function() {
			return _canvasPageLimit;
		},

		/**
		 * Set the canvas page limit
		 * @param {number} count - the canvas max count
		 */
		setCanvasPageLimit: function(count) {
			if (count <= MAX_CANVAS_LIMIT) {
				_canvasPageLimit = count;
			} else {
				console.warn("api.setCanvasPageLimit - exceeds maximum limit of 200 pages.");
			}
		},

		/**
		 * Return the multiplication factor for canvas snapshot quality
		 * @return {number} _canvasQualityFactor
		 */
		getSnapshotQualityFactor: function() {
			return _canvasQualityFactor;
		},

		/**
		 * Set the multiplication factor for canvas snapshots (MIN 1, MAX 5)
		 * @param {number} factor - factor by which to multiply the max dimensions
		 */
		setSnapshotQualityFactor: function(factor) {
			if (!factor || factor < 1) {
				// there is no factor given or it was less than the minimum
				console.warn("No factor was given or the factor was less than 1");

				return;
			} else if (factor > 5) {
				// factor was higher than the maximum
				console.warn("Factor is too high, the max is 5");

				_canvasQualityFactor = 5;
			}
			_canvasQualityFactor = Math.round(factor);
		},

		/**
		 * Return column width for "Sizes" popup dialogs. Return 0 if not defined.
		 * @return {Number}
		 */
		getSizePopupColumnWidth: function(colWidth) {
			return _sizePopupColumnWidth;
		},
		/**
		 * Set width for "Sizes" popup dialogs. Dialog open from Size Charts table, column "Sizes" - little action icon inside the column.
		 * @param {Number} colWidth
		 */
		setSizePopupColumnWidth: function(colWidth) {
			_sizePopupColumnWidth = colWidth;
		},

		getExternalToInternalUrlConverter: function() {
			return _externalToInternalUrlFunction;
		},

		getRemovedImagePanes: function() {
			return _removedImagePanes;
		},

		getImageTypes: function() {
			return _imageTypes;
		},

		hideAction: function(/*String*/type, /*String*/viewId, /*String*/actionId) {
			access.hideResource(type, viewId, actionId);
		},

		hideRevisionAction: function(/*String*/type, /*String*/actionId) {
			access.hideResource(type, "", actionId);
		},

		hideView: function(/*String*/type, /*String*/viewId) {
			access.hideResource(type, viewId, "");
		},

		printWithViewableImage: function() {
			sessionStore.printViewableImage = true;
		},

		setBrowseMapping() {
			apiUtility.deprecated("api.setBrowseMapping is deprecated; please use api.addLinkValidation instead.");
		},

		setExternalToInternalUrlConverter: function(/*Function?*/f) {
			_externalToInternalUrlFunction = f;
		},

		setNumberSeparators() {
			apiUtility.deprecated("api.setNumberSeparators is deprecated; please use the locale settings UI instead.");
		},

		setRelationshipAccess: function(/*String*/type, /*String*/relId, /*Boolean*/bAccess) {
			reflectionHints.setAccess(type, relId, bAccess ? Boolean : false);
		},

		setRelationshipTarget: function(/*String*/type, /*String*/relId, /*String*/targetType, /*Boolean*/bParent) {
			reflectionHints.setTarget(type, relId, targetType, bParent);
		},

		addSpecialPath: function(/*String*/type, /*String*/path) {
			reflectionHints.setSpecialPath(type, path);
		},

		setTheme() {
			apiUtility.deprecated("api.setTheme is deprecated; please go to user settings to change the skin/theme or go to Setup > Company > Company Defaults to change it system wide.");
		},

		/**
		 * Set attribute that is used for given type to store globally unique sort order
		 * @param {string} type
		 * @param {string} idAttr - attribute id
		 */
		setTypeSortAttribute: function(type, idAttribute) {
			var rca = reflection.getTypeAttribute(type, idAttribute);
			if (rca) {
				if (rca.getType() == xml.ATTR_DOUBLE) {
					reflectionHints.setSortAttribute(type, idAttribute);
				} else {
					console.error("setTypeSortAttribute: attribute '" + idAttribute + "' for type '" + type + "' must be of type double");
				}
			} else {
				console.error("setTypeSortAttribute: attribute '" + idAttribute + "' does not exist for type '" + type + "'");
			}
		},

		showFullYear: function(/*String?*/loc) {
			if (!loc || (loc == locale.getLocale())) {
				types.showFullYear();
			}
		},

		watchAttribute: function(/*String*/type, /*String*/idAttribute, /*Function*/f) {
			reflectionHints.watchAttribute(type, idAttribute, f);
		},

		getPrintMargins: function(/*String?*/paper, /*String*/orientation, /*Boolean?*/forPdf) {
			var browser = _getBrowser(forPdf);

			return lang.getObject([browser || DEFAULT_BROWSER, paper || DEFAULT_PAPER, orientation].join("."), false, _margins) ||
				lang.getObject([browser || DEFAULT_BROWSER, DEFAULT_PAPER, orientation].join("."), false, _margins) ||
				lang.getObject([DEFAULT_BROWSER, paper || DEFAULT_PAPER, orientation].join("."), false, _margins) ||
				lang.getObject([DEFAULT_BROWSER, DEFAULT_PAPER, orientation].join("."), false, _margins);
		},

		setPrintMargins: function(/*Number*/l, /*Number*/r, /*Number*/t, /*Number*/b, /*String*/orientation, /*String?*/browser, /*String?*/paper) {
			if (l && r && t && b && orientation) {
				lang.setObject([browser || DEFAULT_BROWSER, paper || DEFAULT_PAPER, orientation].join("."), {w: l + r, h: t + b}, _margins);
			} else {
				console.warn("api.setPrintMargins - invalid input: ", arguments);
			}
		},

		/**
		 * Return height and width scaling for current browser when printing in portrait orientation;
		 * at this time we have no idea why scaling is necessary, but it is
		 * @param {boolean} forPdf
		 * @return {Object|null}
		 */
		getPrintScalingForPortrait: function(forPdf) {
			return _portraitScaling[_getBrowser(forPdf)];
		},

		/**
		 * Set height and width scaling for current browser when printing in portrait orientation
		 * @param {string} browser
		 * @param {number} widthRatio
		 * @param {number} heightRatio
		 */
		setPrintScalingForPortrait: function(browser, widthRatio, heightRatio) {
			if (widthRatio && heightRatio) {
				_portraitScaling[browser] = {h: heightRatio, w: widthRatio};
			} else {
				console.warn("api.setPrintScalingForPortrait - invalid input: ", arguments);
			}
		},

		/**
		 * Return zoom factor to apply to data sheets when printing in portrait orientation; for some reason portrait content does
		 * not fill the entire page as it does in landscape mode (not sure why)
		 * @param {boolean} forPdf
		 * @return {Object}
		 */
		getPrintZoomForPortraitDataSheets: function(forPdf) {
			return _portraitZoom[_getBrowser(forPdf)] || 1;
		},

		/**
		 * Set zoom factor used to print data sheets in portrait orientation; for some reason portrait content does
		 * not fill the entire page as it does in landscape mode (not sure why)
		 * @param {string} browser
		 * @param {number} zoom
		 */
		setPrintZoomForPortraitDataSheets: function(browser, zoom) {
			if (zoom) {
				_portraitZoom[browser] = zoom;
			} else {
				console.warn("api.setPrintZoomForPortraitDataSheets - invalid input: ", arguments);
			}
		},

		setTableNumberOfComments() {
			apiUtility.deprecated("api.setTableNumberOfComments is deprecated; please see api.addCommentPath instead to customize comments.");
		},

		addNodeTypeToLog: function(nodeType) {
			_nodeTypesToLog.push(nodeType);
		},

		getLoggedNodeTypes: function() {
			return _nodeTypesToLog;
		},

		/**
		 * Set threshold at which printing algorithm will stop scaling view to fit on a page
		 * @param {Number} value
		 */
		setMinimumFontScaling: function(value) {
			var n = Number(value);

			if (isNaN(n)) {
				console.warn("api.setMinimumFontScaling - invalid input: ", value);
			} else {
				sessionStore.minimumFontScaling = n;
			}
		},

		/**
		* Customize fields values to persist or not for form Save & New action
		* @param {string} type - node type
		* @param {string} viewId  - view id
		* @param {string} fieldId -  field id
		* @param {boolean} bPersist - true for persist data
		*/
		addFormFieldPersistException: function(type, viewId, fieldId, bPersist) {
			var key = type + "^" + viewId + "^" + fieldId;
			_persistFieldsForSaveAndNew[key] = {
				type: type,
				viewId: viewId,
				fieldId: fieldId,
				bPersist: bPersist || false
			};
		},

		/**
		* Return form field rendering logic for form Save & New action
		* @param {string} type - node type
		* @param {string} viewId  - view id
		* @param {string} fieldId -  field id
		* @return {boolean | null} - if field exists, return boolean value else return null
		*/
		getFormFieldPersistException: function(type, viewId, fieldId) {
			var key = type + "^" + viewId + "^" + fieldId;
			var field = _persistFieldsForSaveAndNew[key];
			return field ? field.bPersist : null;
		},

		/**
		 * Move specified toolbar plugin to secondary toolbar
		 * @param {string} id - plugin id
		 * @param {boolean} bAdd - add ("true")/remove("false") plugin to/from second toolbar
		 */
		setPluginForToolbar2: function(id, bAdd) {
			Toolbar.setPluginForToolbar2(id, bAdd);
		},

		/**
		 * Use legacy comment delete access behavior
		 * @param {boolean} bUseLegacy - false to use the new 6.5 behavior, true to use the pre 6.5 behavior
		 */
		setUseLegacyCommentDeleteAccessBehavior: function(bUseLegacy) {
			_useLegacyCommentDeleteAccessBehavior = bUseLegacy;
		},

		/**
		 * Retrieve setting for Use legacy comment delete access behavior
		 * @return false if the new behavior should be used, true to use legacy comment behavior
		 */
		getUseLegacyCommentDeleteAccessBehavior: function() {
			return _useLegacyCommentDeleteAccessBehavior;
		},

		/**
		 * Set maximum quantity of samples that can be created on Samples Form
		 * @param {Number} maxVal - maximum quantity of samples that can be created in form
		 */
		setSamplesQuantityMax: function(maxVal) {
			_maxQuantitySamples = maxVal;
		},

		/**
		 * Retrieve maximum quantity of samples that can be created on Samples Form
		 * @return {Number} maximum quantity of samples that can be created in form
		 */
		getSamplesQuantityMax: function() {
			return _maxQuantitySamples;
		},

		/**
		 * Sets flag to allow "None" option to be available for SKU creation on
		 * color/size attributes
		 * @param {Boolean} allowOption - true to allow "None" option - false by default
		 */
		allowNoneOptionForSKUCreation: function(allowOption) {
			_allowNoneOptionForSKUCreation = !!allowOption;
		},

		/**
		 * Retrieve a flag to determine if the "None" option should be available for SKU creation
		 * on color/size attributes
		 */
		getAllowNoneOptionForSKUCreation: function() {
			return _allowNoneOptionForSKUCreation;
		},

		setDefaultRememberAdHocFilter: function(allowOption) {
			_filterRemember = !!allowOption;
		},

		getDefaultRememberAdHocFilter: function() {
			return _filterRemember;
		},

		/** 
		 * Set react-window usage threshold for single and multi selects
		 * @param {number} value 
		 */
		setLargeListThreshold: function(value) {
			designSystemSettings.largeListThreshold = value;
		},
	};
});
